"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/*
MIT License

Copyright (c) 2017 sudodoki <smd.deluzion@gmail.com>

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */
// https://github.com/sudodoki/toggle-selection/blob/gh-pages/index.js
function deselectCurrent() {
    const selection = document.getSelection();
    if (!(selection === null || selection === void 0 ? void 0 : selection.rangeCount)) {
        return () => { };
    }
    let active = document.activeElement;
    const ranges = [];
    for (let i = 0; i < selection.rangeCount; i++) {
        ranges.push(selection.getRangeAt(i));
    }
    switch (active === null || active === void 0 ? void 0 : active.tagName.toUpperCase()) { // .toUpperCase handles XHTML
        case 'INPUT':
        case 'TEXTAREA':
            active.blur();
            break;
        default:
            active = null;
            break;
    }
    selection.removeAllRanges();
    return () => {
        selection.type === 'Caret' &&
            selection.removeAllRanges();
        if (!selection.rangeCount) {
            ranges.forEach(range => selection.addRange(range));
        }
        active && active.focus();
    };
}
// https://github.com/sudodoki/copy-to-clipboard/blob/master/index.js
const clipboardToIE11Formatting = {
    'text/plain': 'Text',
    'text/html': 'Url',
    default: 'Text',
};
const defaultMessage = 'Copy to clipboard: #{key}, Enter';
function format(message) {
    const copyKey = `${/mac os x/i.test(navigator.userAgent) ? '⌘' : 'Ctrl'}+C`;
    return message.replace(/#{\s*key\s*}/g, copyKey);
}
function copy(text, options) {
    let reselectPrevious;
    let range;
    let selection;
    let mark;
    let success = false;
    options = options || {};
    const debug = options.debug || false;
    try {
        reselectPrevious = deselectCurrent();
        range = document.createRange();
        selection = document.getSelection();
        mark = document.createElement('span');
        mark.textContent = text;
        // avoid screen readers from reading out loud the text
        mark.ariaHidden = 'true';
        // reset user styles for span element
        mark.style.all = 'unset';
        // prevents scrolling to the end of the page
        mark.style.position = 'fixed';
        mark.style.top = '0px';
        mark.style.clip = 'rect(0, 0, 0, 0)';
        // used to preserve spaces and line breaks
        mark.style.whiteSpace = 'pre';
        // do not inherit user-select (it may be `none`)
        // @ts-ignore
        mark.style.webkitUserSelect = 'text';
        // @ts-ignore
        mark.style.MozUserSelect = 'text';
        // @ts-ignore
        mark.style.msUserSelect = 'text';
        mark.style.userSelect = 'text';
        mark.addEventListener('copy', e => {
            var _a, _b, _c, _d;
            e.stopPropagation();
            if (options === null || options === void 0 ? void 0 : options.format) {
                e.preventDefault();
                if (typeof e.clipboardData === 'undefined') { // IE 11
                    debug && console.warn('unable to use e.clipboardData');
                    debug && console.warn('trying IE specific stuff');
                    (_a = window.clipboardData) === null || _a === void 0 ? void 0 : _a.clearData();
                    // @ts-ignore
                    const _format = clipboardToIE11Formatting[options.format] || clipboardToIE11Formatting.default;
                    (_b = window.clipboardData) === null || _b === void 0 ? void 0 : _b.setData(_format, text);
                }
                else { // all other browsers
                    (_c = e.clipboardData) === null || _c === void 0 ? void 0 : _c.clearData();
                    (_d = e.clipboardData) === null || _d === void 0 ? void 0 : _d.setData(options.format, text);
                }
            }
        });
        document.body.appendChild(mark);
        range.selectNodeContents(mark);
        selection === null || selection === void 0 ? void 0 : selection.addRange(range);
        const successful = document.execCommand('copy');
        if (!successful) {
            throw new Error('copy command was unsuccessful');
        }
        success = true;
    }
    catch (err) {
        debug && console.error('unable to copy using execCommand: ', err);
        debug && console.warn('trying IE specific stuff');
        try {
            window.clipboardData.setData(options.format || 'text', text);
            // options.onCopy && options.onCopy((window as any).clipboardData);
            success = true;
        }
        catch (error) {
            debug && console.error('unable to copy using clipboardData: ', error);
            debug && console.error('falling back to prompt');
            const message = format('message' in options ? options.message || '' : defaultMessage);
            window.prompt(message, text);
        }
    }
    finally {
        if (selection) {
            if (range && typeof selection.removeRange === 'function') {
                selection.removeRange(range);
            }
            else {
                selection.removeAllRanges();
            }
        }
        if (mark) {
            document.body.removeChild(mark);
        }
        reselectPrevious && reselectPrevious();
    }
    return success;
}
exports.default = copy;
